import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Subscribe from "@/pages/Subscribe";
import Pricing from "@/pages/Pricing";
import AddOns from "@/pages/AddOns";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AdminDashboard from "@/pages/AdminDashboard";
import Wizard from "@/pages/Wizard";
import Home from "@/pages/Home";
import Tracker from "@/pages/Tracker";
import Insights from "@/pages/Insights";
import Learn from "@/pages/Learn";
import AskCoach from "@/pages/AskCoach";
import Recipes from "@/pages/Recipes";
import RaceHub from "@/pages/RaceHub";
import RacePack from "@/pages/RacePack";
import RacePackSetup from "@/pages/RacePackSetup";
import RacePackPlan from "@/pages/RacePackPlan";
import CoachRace from "@/pages/CoachRace";
import CoachRaceSetup from "@/pages/CoachRaceSetup";
import CoachRacePlan from "@/pages/CoachRacePlan";
import ErrorBoundary from "@/components/ErrorBoundary";
import InstallPrompt from "@/components/InstallPrompt";
import AppUpdater from "@/components/AppUpdater";
import QuirkyLayer from "@/components/quirks/QuirkyLayer";
import NotFound from "@/pages/NotFound";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
      <Loader2 className="h-6 w-6 animate-spin text-[#004AAD]" />
    </div>
  );
}

function RequireAuth({ children }) {
  const { isAuthed, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!isAuthed) return <Navigate to="/" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { isAuthed, isAdmin, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!isAuthed) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

// Front door: logged-out visitors AND logged-in members without access see the
// unified sign-in / create-account landing; members with active access go
// straight to their fuelling plan. Admins get their dashboard.
function Entry() {
  const { isAuthed, isAdmin, hasAccess, loading, user } = useAuth();
  if (loading) return <FullLoader />;
  if (isAuthed && isAdmin) return <Navigate to="/admin" replace />;
  if (isAuthed && hasAccess) return <Home />;
  // Coaches without their own subscription land in their race-plan area.
  if (isAuthed && user?.role === "coach") return <Navigate to="/coach" replace />;
  // One-off Race Pack buyers (no subscription) land on their race fuelling.
  if (isAuthed && (user?.racePack?.canBuild || user?.racePack?.purchased)) {
    return <Navigate to="/race-pack" replace />;
  }
  if (isAuthed) return <Subscribe />;
  return <Navigate to="/login" replace />;
}

// Plan builder — any member with access, and the admin (opened from the
// dashboard's "Build a Plan" button). Kept off "/" so the dashboard stays the admin's home.
function PlanBuilder() {
  const { isAuthed, hasAccess, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!isAuthed) return <Navigate to="/login" replace />;
  if (!hasAccess) return <Navigate to="/subscribe" replace />;
  return <Wizard />;
}

// Food Tracker — paid add-on, same access rules as the plan builder.
function TrackerRoute() {
  const { isAuthed, hasAccess, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!isAuthed) return <Navigate to="/login" replace />;
  if (!hasAccess) return <Navigate to="/subscribe" replace />;
  return <Tracker />;
}

// Insights — trend visualisations for tracker upgraders only. Same gate as Tracker.
function InsightsRoute() {
  const { isAuthed, hasAccess, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!isAuthed) return <Navigate to="/login" replace />;
  if (!hasAccess) return <Navigate to="/subscribe" replace />;
  return <Insights />;
}

// Recipes hub — a Full-tier feature. Lite members are redirected home.
function RecipesRoute() {
  const { isAuthed, planTier, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!isAuthed) return <Navigate to="/" replace />;
  if (planTier === "lite") return <Navigate to="/" replace />;
  return <Recipes />;
}

// Race Hub — dedicated home for race-week fuelling, checklist, timeline.
// Available to everyone who's logged in; empty-state renders inside the
// page when no race is set, so no plan-tier gate needed.
function RaceHubRoute() {
  const { isAuthed, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!isAuthed) return <Navigate to="/login" replace />;
  return <RaceHub />;
}

// Calendar & notes — merged into the Tracker; keep the old path as a redirect.
function AppRoutes() {
  const { isAuthed, loading } = useAuth();
  if (loading) return <FullLoader />;
  return (
    <Routes>
      <Route path="/login" element={isAuthed ? <Navigate to="/" replace /> : <Login />} />
      {/* Legacy signup routes — folded into the unified Login page. */}
      <Route path="/register" element={<Navigate to="/login" state={{ mode: "register" }} replace />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pricing" element={<Navigate to="/subscribe" replace />} />
      <Route path="/pricing/addons" element={<Navigate to="/subscribe" replace />} />
      <Route path="/subscribe" element={<Subscribe />} />
      <Route path="/payment/success" element={<RequireAuth><PaymentSuccess /></RequireAuth>} />
      <Route path="/payment/cancel" element={<RequireAuth><PaymentCancel /></RequireAuth>} />
      <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
      <Route path="/app" element={<PlanBuilder />} />
      <Route path="/tracker" element={<TrackerRoute />} />
      <Route path="/insights" element={<InsightsRoute />} />
      <Route path="/calendar" element={<Navigate to="/tracker" replace />} />
      <Route path="/learn" element={<RequireAuth><Learn /></RequireAuth>} />
      <Route path="/ask-ai" element={<RequireAuth><AskCoach /></RequireAuth>} />
      <Route path="/recipes" element={<RecipesRoute />} />
      <Route path="/race" element={<RaceHubRoute />} />
      <Route path="/race-pack" element={<RequireAuth><RacePack /></RequireAuth>} />
      <Route path="/race-pack/new" element={<RequireAuth><RacePackSetup /></RequireAuth>} />
      <Route path="/race-pack/:planId/edit" element={<RequireAuth><RacePackSetup /></RequireAuth>} />
      <Route path="/race-pack/:planId" element={<RequireAuth><RacePackPlan /></RequireAuth>} />
      <Route path="/coach" element={<RequireAuth><CoachRace /></RequireAuth>} />
      <Route path="/coach/athletes/:athleteId/new" element={<RequireAuth><CoachRaceSetup /></RequireAuth>} />
      <Route path="/coach/plans/:planId/edit" element={<RequireAuth><CoachRaceSetup /></RequireAuth>} />
      <Route path="/coach/plans/:planId" element={<RequireAuth><CoachRacePlan /></RequireAuth>} />
      <Route path="/" element={<Entry />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <QuirkyLayer />
          </BrowserRouter>
          <Toaster position="bottom-right" theme="system" duration={2500} />
          <InstallPrompt />
          <AppUpdater />
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
